/**
 * Who the hours on a project actually went to.
 *
 * Horizontal bars: names are long, and a horizontal layout lets them be read
 * as labels rather than rotated axis text. One series, so no legend -- the
 * title names the measure and each bar is directly labelled.
 *
 * Ranked longest-first. The question this answers is "who is this project",
 * and the answer is the top three or four names.
 */
import { ChartFrame, EmptyChart, SERIES_1 } from "./chart-primitives";

export interface PersonHours {
  person: string;
  hours: number;
}

/** Beyond this the chart stops being readable; the rest fold into one row. */
const MAX_ROWS = 10;

export function HoursByPerson({
  people,
  title = "Hours by person",
  subtitle,
  onSelect,
}: {
  people: PersonHours[];
  title?: string;
  subtitle?: string;
  /**
   * Drill into one person. Never offered on the folded "N others" row: that
   * row is a sum over several people and has no person to drill into, and a
   * clickable one would open a drill-in about a name nobody holds.
   */
  onSelect?: (person: string) => void;
}) {
  const totals = new Map<string, number>();
  for (const row of people) {
    // People appear once per period; the chart is about the project overall.
    totals.set(row.person, (totals.get(row.person) ?? 0) + row.hours);
  }
  const ranked = [...totals.entries()]
    .map(([person, hours]) => ({ person, hours }))
    .filter((r) => r.hours > 0)
    .sort((a, b) => b.hours - a.hours);

  if (ranked.length === 0) {
    return (
      <ChartFrame title={title} subtitle={subtitle}>
        <EmptyChart message="No hours recorded against this project" />
      </ChartFrame>
    );
  }

  // The tail is summed into one row rather than dropped: the bars have to add
  // up to the project's hours, or the chart understates the work silently.
  const head = ranked.slice(0, MAX_ROWS);
  const tail = ranked.slice(MAX_ROWS);
  const rows: (PersonHours & { folded?: boolean })[] =
    tail.length > 0
      ? [
          ...head,
          {
            person: `${tail.length} others`,
            hours: tail.reduce((sum, r) => sum + r.hours, 0),
            folded: true,
          },
        ]
      : head;

  const max = Math.max(...rows.map((r) => r.hours));

  return (
    <ChartFrame
      title={title}
      subtitle={subtitle}
      footer={
        <span>
          {ranked.length} people,{" "}
          {Math.round(ranked.reduce((s, r) => s + r.hours, 0)).toLocaleString()} hours
        </span>
      }
    >
      <div className="flex flex-col gap-1.5">
        {rows.map((row) => {
          const bar = (
            <>
              {/* The title stays on the NAME, clickable or not: it is what a
                  truncated name is read from, and moving it to the row would
                  take that away from anyone who cannot click. */}
              <span
                className="w-40 shrink-0 truncate text-left text-muted-foreground"
                title={row.person}
              >
                {row.person}
              </span>
              <span className="h-3 min-w-px flex-1">
                <span
                  className="block h-3 rounded-sm"
                  style={{
                    width: `${Math.max(1, (row.hours / max) * 100)}%`,
                    background: SERIES_1,
                  }}
                />
              </span>
              <span className="w-16 shrink-0 text-right tabular-nums">
                {Math.round(row.hours).toLocaleString()}
              </span>
            </>
          );
          // A real button when it is clickable, not a div with a handler: these
          // rows are the only way into the person drill-in, and a keyboard
          // reader has to be able to reach it.
          return onSelect && !row.folded ? (
            <button
              key={row.person}
              type="button"
              onClick={() => onSelect(row.person)}
              className="flex w-full items-center gap-3 rounded-sm text-xs hover:bg-muted/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
            >
              {bar}
            </button>
          ) : (
            <div key={row.person} className="flex items-center gap-3 text-xs">
              {bar}
            </div>
          );
        })}
      </div>
    </ChartFrame>
  );
}
